import { useEffect, useId, useRef } from 'react';
import { Link } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { ErrorBanner } from '../components/ErrorBanner';
import { IntelligenceReportView } from '../components/IntelligenceReportView';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { AdvisorOrb } from '../features/intelligence/AdvisorOrb';
import {
  isReportStale,
  useIntelligenceReport,
  useIntelligenceReports,
  useRequestIntelligenceReport,
} from '../features/intelligence/queries';
import { reportDateLabel, usageLine } from '../lib/intelligence-view';

const REQUESTING_LABEL = 'Tu equipo está revisando el negocio…';
const REQUEST_LABEL = 'Pedir un informe nuevo';
const HISTORY_ORB = 28;

/**
 * Dale Agents' "Informes" (Max and up). Shows the latest stored report in full. Opening the
 * page asks for a new one at most once, only when there is none or the latest
 * is stale; a failed request is never retried on its own.
 */
export function InteligenciaPage() {
  const historyId = useId();
  const reportsQuery = useIntelligenceReports();
  const summaries = reportsQuery.data?.data;
  const usage = reportsQuery.data?.usage;
  const latest = summaries?.[0];
  const latestQuery = useIntelligenceReport(latest?.id);
  const request = useRequestIntelligenceReport();
  const { mutate: requestReport } = request;
  const hasAutoRequested = useRef(false);

  useEffect(() => {
    if (!summaries || hasAutoRequested.current) return;
    hasAutoRequested.current = true;
    if (isReportStale(summaries[0])) requestReport();
  }, [summaries, requestReport]);

  // A report just written is the newest, even before the refreshed list arrives.
  const current = request.data?.data ?? latestQuery.data;
  const earlier = summaries?.slice(1) ?? [];
  const showFirstLoad = reportsQuery.isLoading || (request.isPending && !latest);

  return (
    <main>
      <PageHeader
        title="Informes"
        description="Lo que tus agentes encontraron en tus números de los últimos 90 días."
        actions={
          <button type="button" className="btn small secondary" disabled={request.isPending || !summaries} onClick={() => requestReport()}>
            {request.isPending ? REQUESTING_LABEL : REQUEST_LABEL}
          </button>
        }
      />

      {showFirstLoad ? <LoadingState label={request.isPending ? REQUESTING_LABEL : 'Cargando informes…'} /> : null}
      {reportsQuery.isError ? <>
        <ErrorBanner message={errorMessage(reportsQuery.error)} />
        <button type="button" className="btn small secondary" disabled={reportsQuery.isFetching} onClick={() => void reportsQuery.refetch()}>Reintentar</button>
      </> : null}
      {request.isError ? <ErrorBanner message={errorMessage(request.error)} /> : null}
      {latestQuery.isError ? <ErrorBanner message={errorMessage(latestQuery.error)} /> : null}

      {current ? <IntelligenceReportView report={current} /> : null}

      {earlier.length > 0 ? (
        <section className="section" aria-labelledby={historyId}>
          <h2 className="section-label" id={historyId}>Informes anteriores</h2>
          <div className="group rows">
            {earlier.map((item) => (
              <Link key={item.id} className="row intel-history-row" to="/inteligencia/$reportId" params={{ reportId: item.id }}>
                <AdvisorOrb personaId={item.signer.personaId} size={HISTORY_ORB} />
                <div className="grow">
                  <p className="row-title">{item.headline}</p>
                  <p className="row-sub">{`${item.signer.name} · ${item.signer.title}`}</p>
                </div>
                <span className="intel-history-date">{reportDateLabel(item.createdAt, new Date())}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {usage ? <p className="intel-usage">{usageLine(usage)}</p> : null}
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
